// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { basketSanitize } from '../../actions/basket';
import { requestToggleMyMatchMode } from '../../actions/mymatch';
import { Target } from '../../client';
import { MyMatchAPIErrorIds, MyMatchLocaleErrorIds } from '../../constants/mymatch';
import { useAppContext } from '../../contexts/AppContextProvider';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import useMergeProps from '../../hooks/useMergeProps';
import {
  selectIsMatchCachedMMAPIResponsesInfosValid,
  makeMatchMMErrorIdSelector,
  selectMatchViewMode,
} from '../../selectors/match.selector';
import { makeMatchIsMyMatchAvailableSelector } from '../../selectors/mymatch.selector';
import { selectUserConnected } from '../../selectors/user/userPrimary.selector';
import { MatchViewModes } from '../../types/matchView';
import { showWebsiteLoginForm } from '../../utils';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';

import MatchViewModeSwitch from './MatchViewModeSwitch';

type MatchViewModeSwitchContainerProps = {
  className?: string;
};

const MatchViewModeSwitchContainer = (ownProps: MatchViewModeSwitchContainerProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { setOnLoginSucceed } = useAppContext();
  const { matchId } = useMatchViewContext();
  const selectorProps = useMemo(() => ({ matchId }), [matchId]);

  const matchViewMode = useSelector((state) => selectMatchViewMode(state, selectorProps));
  const selectMatchMMErrorId = makeMatchMMErrorIdSelector();
  const matchMMErrorId = useSelector((state) => selectMatchMMErrorId(state, selectorProps));

  const selectMatchIsMyMatchAvailable = useMemo(() => makeMatchIsMyMatchAvailableSelector(), []);
  const isMyMatchAvailable = useSelector((state) => selectMatchIsMyMatchAvailable(state, selectorProps));
  const isUserConnected = useSelector(selectUserConnected);

  const isMatchCachedMMAPIResponsesInfosValid = useSelector((state) =>
    selectIsMatchCachedMMAPIResponsesInfosValid(state, selectorProps),
  );
  const [showErrorModal, setShowErrorModal] = useState(false);

  const selectedProps = useMemo(() => ({ matchMMErrorId, matchViewMode }), [matchMMErrorId, matchViewMode]);
  const userIsConnectedTimeoutRef = useRef(null);

  useLayoutEffect(() => {
    if (!isMatchCachedMMAPIResponsesInfosValid) {
      dispatch(basketSanitize());
    }
  }, [dispatch, isMatchCachedMMAPIResponsesInfosValid]);

  const onCloseErrorModal = useCallback(() => {
    setShowErrorModal(false);
  }, []);

  const onToggle = useCallback(() => {
    const userIsConnectedCallback = () => {
      if (
        matchViewMode === MatchViewModes.CLASSIC &&
        ~[
          MyMatchAPIErrorIds.UNEXPECTED_ERROR,
          MyMatchAPIErrorIds.MY_MATCH_UNAVAILABLE,
          MyMatchAPIErrorIds.UNHANDLED_SELECTION,
          MyMatchAPIErrorIds.SELECTION_INACTIVE,
          MyMatchAPIErrorIds.CONTRADICTING_SELECTIONS,
          MyMatchLocaleErrorIds.MATCH_WAS_ALREADY_HERE_BEFORE_COMPLETION,
        ].indexOf(matchMMErrorId)
      ) {
        setShowErrorModal(true);
      } else {
        dispatch(requestToggleMyMatchMode({ matchId }));
      }
    };

    if (!isUserConnected) {
      if (Target.__MOBILE__) {
        setOnLoginSucceed(() => () => {
          userIsConnectedTimeoutRef.current = setTimeout(() => {
            userIsConnectedCallback();
          }, 500);
        });

        history.push(
          RoutesRedirectionFactory.getLoginRouteConfig({
            state: { modal: true },
          }),
        );
      } else {
        showWebsiteLoginForm();
      }
    } else {
      userIsConnectedCallback();
    }
  }, [isUserConnected, matchViewMode, matchMMErrorId, dispatch, matchId, setOnLoginSucceed, history]);

  useEffect(() => {
    return () => {
      if (userIsConnectedTimeoutRef.current) {
        clearTimeout(userIsConnectedTimeoutRef.current);
      }
    };
  }, []);

  const onToggleRef = useRef();
  useEffect(() => {
    onToggleRef.current = onToggle;
  }, [onToggle]);

  const otherProps = useMemo(
    () => ({
      toggled: matchViewMode === MatchViewModes.MY_MATCH,
      onToggle,
      showErrorModal,
      onCloseErrorModal,
    }),
    [matchViewMode, onToggle, showErrorModal, onCloseErrorModal],
  );

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  return isMyMatchAvailable ? <MatchViewModeSwitch {...enhancedProps} /> : null;
};

export default MatchViewModeSwitchContainer;
